import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { COMMUNICATION_PREFERENCES_URL } from 'Constants';
import { getServerMessage } from 'Modules/MessageTemplates/Hooks/errorMessage';
import alertToast from 'Util/ToastifyUtils';
import {
	CommunicationPreferences,
	UpdateCommunicationPreferencesPayload,
} from '../Types/CommunicationPreferences';
import { communicationPreferenceKeys } from './communicationPreferenceKeys';

interface Options {
	/** Omit for the signed-in customer's own preferences; pass an id for the staff route. */
	userId?: string;
	/** Staff saves surface their own errors in the modal, so the toast is suppressed there. */
	silent?: boolean;
}

/**
 * Saves a change and takes the server's answer as the new truth.
 *
 * The response is written straight into the cache rather than invalidated: the server recomputes
 * which rows are locked and which channels can send at all, and a change to either should land
 * immediately rather than after a second request.
 */
export const useUpdateCommunicationPreferences = ({ userId, silent }: Options = {}) => {
	const queryClient = useQueryClient();
	const key = userId ? communicationPreferenceKeys.forUser(userId) : communicationPreferenceKeys.mine();

	return useMutation({
		mutationFn: async (payload: UpdateCommunicationPreferencesPayload) => {
			const url = userId ? `${COMMUNICATION_PREFERENCES_URL}/${userId}` : COMMUNICATION_PREFERENCES_URL;
			const { data } = await axios.put<CommunicationPreferences>(url, payload);
			return data;
		},
		onSuccess: data => {
			queryClient.setQueryData(key, data);
		},
		onError: error => {
			console.error(error);
			if (!silent) {
				alertToast(getServerMessage(error) ?? 'Could not save your preferences', 'error');
			}
			queryClient.invalidateQueries({ queryKey: key });
		},
	});
};
